/**
 * MFX OS — shared UI primitives: status chips, track strip, lineage path,
 * append-only timeline, gate board, modal. Blueprint §2 (universal record UX).
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { statusColor, type Gate, type GateCode, type GateStatus, type OsEvent, type StatusTracks } from './types';
import { useOsList } from './core';
import type { GatePatch } from './commands';

export function Chip({ label, tone }: { label: string; tone?: string }) {
  const c = tone || statusColor(label);
  return (
    <span className="os-chip" style={{ color: c, borderColor: c.startsWith('var') ? c : c, background: 'transparent' }}>
      {label.replace(/_/g, ' ')}
    </span>
  );
}

const TRACK_LABELS: [keyof StatusTracks, string][] = [
  ['commercial', 'Commercial'], ['production', 'Production'], ['fulfillment', 'Fulfillment'],
  ['invoice', 'Invoice'], ['payment', 'Payment'],
];

export function Tracks({ tracks, compact }: { tracks?: StatusTracks; compact?: boolean }) {
  if (!tracks) return null;
  return (
    <div className="os-tracks">
      {TRACK_LABELS.map(([k, label]) => (
        <span key={k} className="os-track" title={`${label}: ${tracks[k]}`}>
          {!compact && <em>{label}</em>}
          <Chip label={String(tracks[k] || '—')} />
        </span>
      ))}
    </div>
  );
}

export interface Crumb { label: string; to?: string }

export function Lineage({ parts }: { parts: Crumb[] }) {
  return (
    <div className="os-lineage">
      {parts.map((p, i) => (
        <span key={i} className="os-crumb">
          {i > 0 && <span className="os-crumb-sep">›</span>}
          {p.to ? <Link to={p.to}>{p.label}</Link> : <span>{p.label}</span>}
        </span>
      ))}
    </div>
  );
}

export function Timeline({ entityId }: { entityId: string }) {
  const { rows, loading } = useOsList<OsEvent>('osEvents', { wheres: [['entityId', '==', entityId]], limit: 200 }, [entityId]);
  const sorted = [...rows].sort((a, b) => (b.at || '').localeCompare(a.at || ''));
  if (loading) return <div className="loading"><div className="spinner" /> Loading activity…</div>;
  if (!sorted.length) return <div className="empty">No activity yet.</div>;
  return (
    <div className="os-timeline">
      {sorted.map((e, i) => (
        <div key={i} className="os-tl-row">
          <div className="os-tl-time">{new Date(e.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
          <div className="os-tl-dot" style={{ background: statusColor(e.to || e.action) }} />
          <div className="os-tl-body">
            <div className="os-tl-action">{e.action}{e.from && e.to ? <span className="os-tl-delta"> {e.from} → {e.to}</span> : null}</div>
            {e.reason && <div className="os-tl-reason">{e.reason}</div>}
            {e.evidence && <div className="os-tl-reason">📎 {e.evidence}</div>}
            <div className="os-tl-actor">{e.actor?.name || e.actor?.email}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function Modal({ open, title, onClose, children, wide }: { open: boolean; title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  if (!open) return null;
  return (
    <div className="os-modal-veil" onClick={onClose}>
      <div className={`os-modal${wide ? ' wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="os-modal-head">
          <div className="os-modal-title">{title}</div>
          <button className="os-x" onClick={onClose}>✕</button>
        </div>
        <div className="os-modal-body">{children}</div>
      </div>
    </div>
  );
}

// ── Gate board (blueprint stage 7) ───────────────────────────────────────
const GATE_ACTIONS: { status: GateStatus; label: string }[] = [
  { status: 'ready', label: '✓ Ready' },
  { status: 'in_review', label: 'In review' },
  { status: 'waiting', label: 'Waiting' },
  { status: 'blocked', label: '⛔ Blocked' },
  { status: 'not_applicable', label: 'N/A' },
];

export function GateBoard({ gates, onPatch, busy }: { gates: Gate[]; onPatch: (code: GateCode, patch: GatePatch) => void; busy?: boolean }) {
  const [openGate, setOpenGate] = useState<string>('');
  const [evidence, setEvidence] = useState('');
  const [reason, setReason] = useState('');
  const [risk, setRisk] = useState('');
  const [overriding, setOverriding] = useState(false);

  const act = (g: Gate, status: GateStatus) => {
    if (status === 'blocked' && !reason.trim()) { alert('Blocked needs a reason.'); return; }
    onPatch(g.code, { status, evidence: evidence.trim() || undefined, blockerReason: status === 'blocked' ? reason.trim() : undefined });
    setOpenGate(''); setEvidence(''); setReason(''); setOverriding(false);
  };
  const doOverride = (g: Gate) => {
    if (!reason.trim() || !risk.trim()) { alert('Override needs a reason AND a risk note.'); return; }
    onPatch(g.code, { status: 'overridden', override: { reason: reason.trim(), riskNote: risk.trim(), approvedBy: '', approvedAt: '' } });
    setOpenGate(''); setReason(''); setRisk(''); setOverriding(false);
  };

  return (
    <div className="os-gates">
      {gates.map((g) => {
        const open = openGate === g.code;
        return (
          <div key={g.code} className={`os-gate${open ? ' open' : ''}`} style={{ borderLeftColor: statusColor(g.status) }}>
            <button className="os-gate-head" onClick={() => { setOpenGate(open ? '' : g.code); setEvidence(''); setReason(''); setRisk(''); setOverriding(false); }}>
              <span className="os-gate-name">{g.label}</span>
              <span className="os-gate-owner">{g.ownerRole}</span>
              <Chip label={g.status} />
            </button>
            {(g.blockerReason || g.evidence || g.override) && !open && (
              <div className="os-gate-note">
                {g.blockerReason ? `⛔ ${g.blockerReason}` : g.override ? `⚠ overridden: ${g.override.reason}` : `📎 ${g.evidence}`}
              </div>
            )}
            {open && (
              <div className="os-gate-editor">
                {!overriding ? (
                  <>
                    <input placeholder="Evidence / note (what satisfies this gate?)" value={evidence} onChange={(e) => setEvidence(e.target.value)} />
                    <input placeholder="Blocker reason (required if blocking)" value={reason} onChange={(e) => setReason(e.target.value)} />
                    <div className="os-gate-actions">
                      {GATE_ACTIONS.map((a) => (
                        <button key={a.status} className="btn sm" disabled={busy} onClick={() => act(g, a.status)}>{a.label}</button>
                      ))}
                      <button className="btn sm warn" disabled={busy} onClick={() => setOverriding(true)}>Override…</button>
                    </div>
                  </>
                ) : (
                  <>
                    <input placeholder="Override reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
                    <input placeholder="Risk note (required)" value={risk} onChange={(e) => setRisk(e.target.value)} />
                    <div className="os-gate-actions">
                      <button className="btn sm warn" disabled={busy} onClick={() => doOverride(g)}>Confirm override</button>
                      <button className="btn sm" onClick={() => setOverriding(false)}>Back</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function Blockers({ items }: { items: string[] }) {
  if (!items.length) return <div className="os-clear">✓ All hard rules satisfied — ready to approve.</div>;
  return (
    <div className="os-blockers">
      <div className="os-blockers-title">⛔ {items.length} hard rule{items.length > 1 ? 's' : ''} blocking approval</div>
      {items.map((b, i) => <div key={i} className="os-blocker">• {b}</div>)}
    </div>
  );
}
